// frontend/src/ShopifyGiftCards/ShopifyGiftCards.jsx — /shopify-gift-cards: every job, newest first.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePinnedHeights } from '../ItAssets/ItAssets';
import { useAuth } from '../AuthComponent';
import { sgcApi, openBlob } from './apiClient';
import { Alert, JobPill, Progress, expiryText, money, useList, when } from './common';

// One store's logo for the printed sheet: the thumbnail (fetched with the login, so not a plain <img src>), Upload, Remove.
function LogoTile({ row, onChanged }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  useEffect(() => {
    if (!row.has_logo) { setUrl(null); return undefined; }
    let u; sgcApi.logo(row.id).then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => setUrl(null));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [row.id, row.has_logo, row.updated_at]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try { const r = await sgcApi.putLogo(row.id, file); if (r.warning) setErr(r.warning); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async () => { setBusy(true); setErr(''); try { await sgcApi.removeLogo(row.id); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  return (
    <div className={`uh-sgc-logo${row.test ? ' test' : ''}`}>
      <div className="uh-sgc-logo-img">{url ? <img src={url} alt={`${row.name} logo`} /> : <span className="uh-faint">Soccer Post logo (default)</span>}</div>
      <div className="uh-sgc-logo-name">{row.name}{row.test ? <span className="uh-ia-tag" style={{ marginLeft: 6 }}>test</span> : null}</div>
      <div className="uh-sgc-logo-acts">
        <button type="button" className="uh-btn slim" disabled={busy} onClick={() => input.current && input.current.click()}>{row.has_logo ? 'Replace' : 'Upload'}</button>
        {row.has_logo && <button type="button" className="uh-btn slim" disabled={busy} onClick={remove}>Remove</button>}
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
      </div>
      {err && <div className="uh-ia-bad" style={{ fontSize: 12 }}>{err}</div>}
    </div>
  );
}

export default function ShopifyGiftCards() {
  const navigate = useNavigate();
  const { nameOf } = useAuth();
  const { data, error, reload } = useList(() => sgcApi.jobs(), []);
  const { data: meta } = useList(() => sgcApi.meta(), []);
  useEffect(() => { if (!data || !data.running) return undefined; const t = setInterval(reload, 3000); return () => clearInterval(t); }, [data, reload]);
  const setPage = usePinnedHeights();
  const jobs = (data && data.jobs) || [];
  const storeName = (id) => ((meta && meta.stores) || []).find((s) => s.id === id)?.name || id.replace('.myshopify.com', '');
  const cards = jobs.reduce((n, j) => n + Number(j.cards_created), 0);
  const value = jobs.reduce((n, j) => n + Number(j.total_value), 0);
  const print = async (j) => { try { openBlob(await sgcApi.pdf(j.id), `shopify-gift-cards-job-${j.id}.pdf`); } catch (e) { window.alert(e.message); } };
  const { data: logoData, reload: reloadLogos } = useList(() => sgcApi.logos(), []);
  const [showLogos, setShowLogos] = useState(false);
  return (
    <section className="uh-page uh-ia-page" ref={setPage}>
      <div className="uh-ia-top">
        <div className="uh-ph">
          <div>
            <h1 className="uh-h1">Gift Cards</h1>
            <div className="uh-sub">Creates Shopify gift cards on any of our stores, one or many at a time, with an expiry and a note on every card. Every creation is a job you can reopen, print and check.</div>
          </div>
          {data && (
            <div className="uh-ia-stats">
              {[[jobs.length, 'Jobs'], [cards, 'Cards created'], [money(value), 'Total value']].map(([v, k]) => (
                <div className="stat" key={k}><span className="v uh-num">{typeof v === 'number' ? v.toLocaleString() : v}</span><span className="k">{k}</span></div>))}
            </div>
          )}
        </div>
      </div>
      <Alert>{error}</Alert>
      {data && !data.production && <div className="uh-kpc-banner info">This server is not on the production database, so jobs here are dry runs: nothing reaches Shopify.</div>}
      <div className="uh-card">
        <div className="uh-card-h"><h2 className="uh-h2 lg">Jobs</h2><span className="uh-sp" />
          <button type="button" className="uh-btn primary" disabled={data && data.running} title={data && data.running ? 'Wait for the running job to finish' : ''} onClick={() => navigate('/shopify-gift-cards/new')}>New job</button></div>
        <div className="uh-tscroll"><table className="uh-tbl">
          <thead><tr><th>#</th><th>Started</th><th>By</th><th>Store</th><th>Purpose</th><th className="r">Cards</th><th>Expires</th><th>Progress</th><th>Status</th><th /></tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="uh-row-link" onClick={() => navigate(`/shopify-gift-cards/jobs/${j.id}`)}>
                <td className="uh-mono">#{j.id}</td><td className="uh-ia-nowrap">{when(j.created_at)}</td><td>{nameOf(j.created_by)}</td><td>{storeName(j.store)}</td><td>{j.purpose_text}</td>
                <td className="r uh-num">{Math.abs(Number(j.total_value) - j.count * Number(j.amount)) < 0.005 ? <>{j.count} × {money(j.amount)}</> : <>{j.count} cards, {money(j.total_value)}</>}</td>
                <td className="uh-ia-nowrap">{expiryText(j.expires_on)}</td>
                <td><Progress done={Number(j.cards_created)} total={Number(j.cards_total)} failed={Number(j.cards_failed)} cancelled={Number(j.cards_cancelled || 0)} /></td>
                <td><JobPill job={j} />{j.dry_run && <span className="uh-ia-tag" style={{ marginLeft: 6 }}>Dry run</span>}</td>
                <td className="r uh-ia-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="uh-btn slim" onClick={() => navigate(`/shopify-gift-cards/jobs/${j.id}`)}>Open</button>{' '}
                  <button type="button" className="uh-btn slim" disabled={!Number(j.cards_created)} onClick={() => print(j)}>Print</button></td>
              </tr>))}
            {data && !jobs.length && <tr><td colSpan={10}><div className="uh-empty">No jobs yet. A job creates one or many gift cards on a Shopify store and keeps their codes here.</div></td></tr>}
          </tbody></table></div>
      </div>
      <div className="uh-card">
        <div className="uh-card-h"><h2 className="uh-h2 lg">Store logos on the printed sheet</h2><span className="uh-sp" />
          <span className="uh-muted" style={{ marginRight: 10 }}>{logoData ? `${logoData.logos.filter((l) => l.has_logo).length} of ${logoData.logos.length} stores have their own; the rest print the Soccer Post logo` : ''}</span>
          <button type="button" className="uh-btn slim" onClick={() => setShowLogos((v) => !v)}>{showLogos ? 'Hide' : 'Show'}</button></div>
        {showLogos && logoData && (
          <div className="uh-card-b">
            <div className="uh-ia-hint" style={{ marginBottom: 10 }}>A PNG with a transparent background prints best; a white-on-transparent logo will not show on the white card. The logo goes at the top of every card of that store's jobs, at about 1.9 by 0.5 inches.</div>
            <div className="uh-sgc-logos">{logoData.logos.map((row) => <LogoTile key={row.id} row={row} onChanged={reloadLogos} />)}</div>
          </div>
        )}
      </div>
    </section>
  );
}
